import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, getUser } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { PLATFORMS } from "../lib/brand";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Brain, CheckCircle2, Loader2, Link as LinkIcon } from "lucide-react";

const LOADER_BG =
  "https://images.unsplash.com/photo-1760810650339-455f4daf5b81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjByZWQlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzc2NDg5NzAzfDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  "Cleaning data…",
  "Removing duplicates…",
  "Calculating sales & orders…",
  "Ranking SKUs…",
  "Building ads plan…",
  "Analysing cities…",
  "Running AI narrative via Claude…",
  "Assembling Excel sheets…",
];

// Speed of the progress animation.
// Backend now returns `ready` in ~300-500ms, so the animation is purely
// cosmetic — keep it snappy so it never blocks the user's flow.
const STEP_INTERVAL_MS = 180;

export default function UploadPage() {
  const nav = useNavigate();
  const user = getUser();
  const allowed = (user?.allowed_platforms && user.allowed_platforms.length > 0)
    ? user.allowed_platforms
    : PLATFORMS.map((p) => p.id);
  const allowedPlatforms = PLATFORMS.filter((p) => allowed.includes(p.id));
  const [platform, setPlatform] = useState(allowedPlatforms[0]?.id || "Myntra");
  const [brandUrl, setBrandUrl] = useState("");
  const [file, setFile] = useState(null);
  // Optional inventory sheet — when present we POST it after the main upload
  // finalises so the report's Inventory Alerts can surface closed/finished SKUs.
  const [inventoryFile, setInventoryFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [uploadPct, setUploadPct] = useState(0);
  const [processElapsed, setProcessElapsed] = useState(0);
  const inputRef = useRef(null);
  const inventoryInputRef = useRef(null);

  useEffect(() => {
    if (!processing) return;
    const id = setInterval(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), STEP_INTERVAL_MS);
    const t = Date.now();
    const et = setInterval(() => setProcessElapsed(Math.floor((Date.now() - t) / 1000)), 250);
    return () => { clearInterval(id); clearInterval(et); };
  }, [processing]);

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  }

  async function handleUpload() {
    if (!file) return toast.error("Choose a CSV or XLSX file.");
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["csv", "xlsx", "xls"].includes(ext)) return toast.error("Only CSV/XLSX supported.");
    if (file.size > 60 * 1024 * 1024) return toast.error("File is too large. Max 60 MB.");
    const url = brandUrl.trim();
    if (!url) return toast.error("Please paste your brand store link.");
    const expectedHost = {
      Myntra: "myntra.com",
      Blinkit: "blinkit.com",
      Nykaa: "nykaa.com",
      "Swiggy Instamart": "swiggy.com",
    }[platform];
    if (expectedHost && !url.toLowerCase().includes(expectedHost)) {
      return toast.error(`Link must be on ${expectedHost}`);
    }

    setUploading(true);
    setUploadPct(0);
    try {
      // 1) init session
      const { data: init } = await api.post("/seller/upload/init", {
        platform,
        brand_live_url: url,
        filename: file.name,
        total_size: file.size,
      });
      const sessionId = init.session_id;
      const chunkSize = init.chunk_size_bytes || 2 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);

      // 2) upload chunks sequentially
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("session_id", sessionId);
        fd.append("chunk_index", String(i));
        fd.append("chunk", blob, `chunk_${i}`);
        await api.post("/seller/upload/chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 25000,
        });
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
      }

      // 3) finalize
      const { data: up } = await api.post("/seller/upload/finalize", { session_id: sessionId });

      // 3b) optional inventory attach — best-effort, non-fatal.
      if (inventoryFile) {
        try {
          const ifd = new FormData();
          ifd.append("upload_id", up.id);
          ifd.append("file", inventoryFile);
          const { data: invResp } = await api.post("/seller/upload/inventory", ifd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          });
          toast.success(`Inventory attached · ${invResp.inventory_rows} rows`);
        } catch (invErr) {
          toast.error(errMsg(invErr, "Inventory upload failed — continuing without it"));
        }
      }

      toast.success(`Uploaded ${up.row_count} rows · live catalogue fetching in background.`);
      setUploading(false);
      setProcessing(true);
      setStepIdx(0);

      // 4) fire report generation — returns immediately with status=processing
      const { data: rep } = await api.post("/seller/reports/generate", { upload_id: up.id });

      // 5) poll until the report is ready (or failed). The backend
      // returns `ready` in ~300-500ms so we poll aggressively:
      // check immediately, then every 400ms. Total wall-time from click
      // to navigation is typically <2s.
      const started = Date.now();
      const MAX_WAIT = 60 * 1000;
      let finalRep = null;
      let firstCheck = true;
      while (Date.now() - started < MAX_WAIT) {
        if (!firstCheck) await new Promise((r) => setTimeout(r, 400));
        firstCheck = false;
        try {
          const { data } = await api.get(`/seller/reports/${rep.id}`);
          if (data.status === "ready") {
            finalRep = data;
            // Snap the progress animation to "done" instantly so the
            // user never sees a hanging spinner even if the backend
            // was faster than the animation.
            setStepIdx(STEPS.length - 1);
            break;
          }
          if (data.status === "failed") {
            throw new Error(data.error || "Report generation failed");
          }
        } catch (e) {
          // keep polling on transient errors
        }
      }
      if (!finalRep) throw new Error("Report took too long. Please check the Reports page in a minute.");
      toast.success("AI Growth Report generated");
      setProcessing(false);
      nav(`/seller/reports?highlight=${rep.id}`);
    } catch (err) {
      setUploading(false);
      setProcessing(false);
      toast.error(errMsg(err, "Upload failed"));
    }
  }

  return (
    <AppShell role="seller">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Upload</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">
          Drop data. Get growth plan.
        </h1>
      </div>

      {/* Platform selector */}
      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white mb-6">
        <div className="text-sm font-bold uppercase tracking-[0.2em] mb-4">1. Select Platform</div>
        {allowedPlatforms.length === 0 && (
          <div className="text-sm text-slate-400 bg-white/[0.02] p-3 rounded-md" data-testid="no-platforms-warning">
            Your account has no platform access yet. Please contact your admin to enable at least one of Myntra / Blinkit / Nykaa.
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {allowedPlatforms.map((p) => (
            <button
              key={p.id}
              onClick={() => setPlatform(p.id)}
              data-testid={`platform-${p.id.replaceAll(' ', '-').toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border-2 transition-all ${
                platform === p.id
                  ? "border-[#4F46E5] bg-[#4F46E5] text-white"
                  : "border-white/[0.08] text-slate-300 hover:border-zinc-400"
              }`}
            >
              {p.id}
            </button>
          ))}
        </div>
      </div>

      {/* Brand Store Link */}
      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white mb-6" data-testid="brand-url-section">
        <div className="text-sm font-bold uppercase tracking-[0.2em] mb-2">2. Brand Store Link</div>
        <p className="text-xs text-slate-500 mb-3">
          Paste your live brand/store URL on {platform}. We'll fetch your catalogue to enrich the report with
          price audit, out-of-stock alerts, dead-SKU detection, real product images, and SEO tips.
        </p>
        <div className="flex items-center gap-2 border border-white/[0.08] rounded-md px-3 py-2 focus-within:border-[#4F46E5]">
          <LinkIcon size={14} className="text-slate-500 shrink-0" />
          <input
            type="url"
            required
            value={brandUrl}
            onChange={(e) => setBrandUrl(e.target.value)}
            placeholder={
              platform === "Myntra"
                ? "https://www.myntra.com/<your-brand>"
                : platform === "Blinkit"
                ? "https://blinkit.com/<your-store-or-product>"
                : platform === "Nykaa"
                ? "https://www.nykaa.com/brand/<your-brand>/..."
                : "https://www.swiggy.com/instamart/..."
            }
            className="flex-1 text-sm focus:outline-none bg-transparent"
            data-testid="brand-url-input"
          />
        </div>
      </div>

      {/* Dropzone */}
      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white mb-6">
        <div className="text-sm font-bold uppercase tracking-[0.2em] mb-4">3. Upload File</div>
        <div className="mb-4 p-3 bg-white/[0.02] rounded-md text-[11px] text-slate-400 leading-relaxed" data-testid="column-hints">
          <span className="font-bold uppercase tracking-widest text-[#4F46E5]">Tip —</span> any CSV/XLSX works. For best results include one of:
          <span className="font-mono-data block mt-1">sku, order_id, qty (or orders), price (or amount / total / sales), city, date, article_type, status, cancellation_reason.</span>
        </div>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-all ${
            dragOver ? "border-[#4F46E5] bg-[#4F46E5]/5" : "border-white/[0.1] hover:border-zinc-500"
          }`}
          data-testid="upload-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="upload-file-input"
          />
          {file ? (
            <div className="flex flex-col items-center">
              <FileSpreadsheet size={36} className="text-[#4F46E5]" />
              <div className="mt-3 font-display text-xl font-black tracking-tighter">{file.name}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono-data">
                {file.size < 1024 * 1024
                  ? `${(file.size / 1024).toFixed(1)} KB`
                  : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                {" — "}{platform}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <UploadCloud size={36} className="text-slate-500" />
              <div className="mt-3 font-display text-xl font-black tracking-tighter">Drop CSV or XLSX here</div>
              <div className="text-xs text-slate-500 mt-1">or click to browse</div>
            </div>
          )}
        </div>

        <button
          onClick={handleUpload}
          disabled={!file || uploading || processing}
          className="mt-6 w-full btn-primary disabled:opacity-50"
          data-testid="upload-submit-btn"
        >
          {uploading ? (
            <><Loader2 size={14} className="inline animate-spin mr-1" /> Uploading… {uploadPct}%</>
          ) : processing ? (
            <><Loader2 size={14} className="inline animate-spin mr-1" /> Generating report…</>
          ) : (
            "Upload & Generate AI Report"
          )}
        </button>
      </div>

      {/* Optional inventory file — closed / finished SKU details flow into
          Insights → Inventory Alerts after the report is generated. */}
      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white mb-6" data-testid="inventory-upload-section">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold uppercase tracking-[0.2em]">
            4. Inventory Sheet <span className="text-[10px] text-slate-500 normal-case tracking-normal ml-2">(optional)</span>
          </div>
          {inventoryFile && (
            <button
              onClick={() => { setInventoryFile(null); if (inventoryInputRef.current) inventoryInputRef.current.value = ""; }}
              className="text-[11px] text-[#4F46E5] hover:underline"
              data-testid="inventory-clear-btn"
            >
              Remove
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Upload your Myntra inventory export (CSV/XLSX) to see <b className="text-white/80">closed listings</b> and
          <b className="text-white/80"> finished/out-of-stock SKUs</b> in the report. We auto-detect a status column
          named <span className="font-mono-data">inventory_status</span>, <span className="font-mono-data">sku_status</span>,
          <span className="font-mono-data"> listing_status</span> or <span className="font-mono-data">status</span>.
        </p>
        <div
          onClick={() => inventoryInputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all ${
            inventoryFile ? "border-[#4F46E5]/60 bg-[#4F46E5]/5" : "border-white/[0.1] hover:border-zinc-500"
          }`}
          data-testid="inventory-upload-dropzone"
        >
          <input
            ref={inventoryInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => setInventoryFile(e.target.files?.[0] || null)}
            data-testid="inventory-file-input"
          />
          {inventoryFile ? (
            <div className="flex flex-col items-center">
              <FileSpreadsheet size={28} className="text-[#4F46E5]" />
              <div className="mt-2 text-sm font-bold tracking-tight">{inventoryFile.name}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono-data">
                {inventoryFile.size < 1024 * 1024
                  ? `${(inventoryFile.size / 1024).toFixed(1)} KB`
                  : `${(inventoryFile.size / 1024 / 1024).toFixed(2)} MB`}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <UploadCloud size={24} className="text-slate-500" />
              <div className="mt-2 text-sm">Click to attach inventory sheet</div>
              <div className="text-xs text-slate-500 mt-1">CSV / XLSX · max 25 MB</div>
            </div>
          )}
        </div>
      </div>

      {/* Processing overlay */}
      {processing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D0D0D]/85 backdrop-blur-2xl" data-testid="processing-overlay">
          <div
            className="absolute inset-0 opacity-40"
            style={{ backgroundImage: `url(${LOADER_BG})`, backgroundSize: "cover" }}
          />
          <div className="relative max-w-lg w-full mx-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#4F46E5] flex items-center justify-center">
                <Brain size={22} className="text-white" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">AI Processing</div>
                <div className="font-display text-2xl font-black tracking-tighter">EcomCatalog AI is thinking…</div>
              </div>
            </div>
            <div className="space-y-2 font-mono-data text-sm">
              {STEPS.map((s, i) => (
                <div
                  key={s}
                  className={`flex items-center gap-2 transition-all ${
                    i < stepIdx ? "text-white/50" : i === stepIdx ? "text-white" : "text-white/20"
                  }`}
                >
                  {i < stepIdx ? (
                    <CheckCircle2 size={14} className="text-[#4F46E5]" />
                  ) : i === stepIdx ? (
                    <Loader2 size={14} className="animate-spin text-[#4F46E5]" />
                  ) : (
                    <span className="w-3.5 h-3.5 rounded-full border border-white/30 inline-block" />
                  )}
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 text-xs text-white/40 terminal-blink font-mono-data">claude-sonnet-4-5 &gt; streaming narrative</div>
            <div className="mt-2 text-[10px] text-white/35 font-mono-data flex items-center gap-2" data-testid="processing-elapsed">
              <span>elapsed: {processElapsed}s</span>
              {processElapsed >= 15 && (
                <span className="text-amber-300">
                  · slower than usual — you can safely leave this page and check{" "}
                  <a href="/seller/reports" className="underline hover:text-white">Reports</a> in a moment
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
